// ─────────────────────────────────────────────────────────────────────────────
// Paginación de las tablas de Reportería: navegación de página + selector de tamaño.
//
// El prototipo la trae en 8 tablas y la pantalla solo la tenía en «Campañas General». Esto extrae ese
// patrón —que ya existía ahí— para poder usarlo en el resto sin reescribir la aritmética en cada tab.
//
// El escalonado es 10 · 25 · 50 · 100 · 200, con 10 por omisión. El techo de 200 no es arbitrario: es
// el «Top 200» del que hablan las hojas «Definiciones» del cliente («Top 200 Más Reconocidos», «Top
// 200 Reconocedores»), así que es el número máximo de filas que alguien tiene una razón declarada para
// querer ver de una vez.
//
// Hay DOS modos, y la diferencia es de dónde sale el recorte:
//
//   · Modo normal — el backend ya devolvió todas las filas y el recorte es del cliente. Navegación de
//     páginas + rango («11–20 de 47») + selector. Lo usan Históricos, Líderes y Moderación.
//   · `soloTamano` — el tamaño manda el `top` de la PETICIÓN. El backend no tiene offset, así que no
//     existe «página 2» que ofrecer: pintar los botones deshabilitados para siempre sugeriría que hay
//     más y que algo no funciona. Lo usan los Top del Diario y el ranking de Brillamos, cuyos handlers
//     aceptan `Top` (10 por omisión, techo 200) desde el 24-ago-2026.
// ─────────────────────────────────────────────────────────────────────────────
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

/** Escalonado estándar. 10 primero porque es el valor por omisión. */
export const TAMANOS_DE_PAGINA = [10, 25, 50, 100, 200] as const;

export const TAMANO_DE_PAGINA_POR_OMISION = 10;

/**
 * Estado y aritmética de una tabla paginada. Devuelve la porción visible y lo que el control necesita.
 *
 * Cambiar el tamaño de página vuelve a la página 1: si alguien está en la 7 de 10 por página y pasa a
 * 100, la página 7 ya no existe y quedaría mirando una tabla vacía sin entender por qué.
 */
export function usePaginacion<T>(filas: readonly T[]) {
  const [pagina, setPagina] = useState(1);
  const [tamano, setTamano] = useState<number>(TAMANO_DE_PAGINA_POR_OMISION);

  const totalPaginas = Math.max(1, Math.ceil(filas.length / tamano));
  // Si el filtro de arriba reduce las filas mientras estamos en una página alta, se recorta en vez de
  // dejar la tabla en blanco.
  const paginaSegura = Math.min(pagina, totalPaginas);

  const visibles = useMemo(
    () => filas.slice((paginaSegura - 1) * tamano, paginaSegura * tamano),
    [filas, paginaSegura, tamano],
  );

  return {
    visibles,
    pagina: paginaSegura,
    totalPaginas,
    total: filas.length,
    tamano,
    setPagina,
    cambiarTamano: (nuevo: number) => { setTamano(nuevo); setPagina(1); },
  };
}

export interface PaginacionProps {
  readonly pagina: number;
  readonly totalPaginas: number;
  readonly total: number;
  readonly tamano: number;
  readonly onPagina: (p: number) => void;
  readonly onTamano: (t: number) => void;
  /**
   * Solo el selector de tamaño, sin navegación de páginas ni rango de filas.
   *
   * Es lo que corresponde cuando el tamaño manda el `top` de la petición y el backend no tiene
   * offset: no existe «página 2» que ofrecer, y pintar los botones deshabilitados para siempre
   * sugeriría que hay más y que algo no funciona. Lo usan los Top del Diario y el ranking de
   * Brillamos.
   */
  readonly soloTamano?: boolean;
  /**
   * Pinta el pie AUNQUE haya pocas filas.
   *
   * 🔴 Existe por un reporte de UAT: los dos Top del Diario van lado a lado, y con 13 filas a la
   * izquierda y 7 a la derecha solo aparecía el pie de la izquierda. Cada tabla por separado hacía lo
   * correcto —un paginador sobre 7 filas es ruido—, pero EN PAREJA la asimetría se lee como que el de
   * la derecha está roto. Con las dos tablas a la vista, la consistencia pesa más que el ruido.
   */
  readonly siempreVisible?: boolean;
}

export function Paginacion({
  pagina, totalPaginas, total, tamano, onPagina, onTamano,
  soloTamano = false, siempreVisible = false,
}: PaginacionProps) {
  const { t } = useTranslation('recognition');

  /*
   * Con menos filas que el tamaño mínimo no se pinta nada: un control de paginación sobre tres filas
   * es ruido, y el selector de tamaño no tendría ningún efecto observable.
   *
   * 🔴 En modo `soloTamano` esa regla NO aplica. Ahí el tamaño manda cuántas filas PIDE la consulta,
   * así que con 10 filas a la vista el selector es justo lo que hace falta para poder pedir más — si
   * se escondiera, la pantalla se quedaría topada en 10 sin ninguna forma de salir.
   */
  if (!soloTamano && !siempreVisible && total <= TAMANO_DE_PAGINA_POR_OMISION) return null;

  const desde = (pagina - 1) * tamano + 1;
  const hasta = Math.min(pagina * tamano, total);

  return (
    <div className="rr-pagination">
      {!soloTamano && (
        <span className="rr-pagination-label">
          {t('reports.common.showingRange', { desde, hasta, total })}
        </span>
      )}

      {!soloTamano && (
      <div className="rr-pagination-nav">
        <button
          type="button"
          className="rr-btn rr-btn--ghost"
          onClick={() => onPagina(Math.max(1, pagina - 1))}
          disabled={pagina <= 1}
        >
          {t('reports.campaigns.prevPage')}
        </button>
        <span className="rr-pagination-label">
          {t('reports.campaigns.pageOf', { page: pagina, total: totalPaginas })}
        </span>
        <button
          type="button"
          className="rr-btn rr-btn--ghost"
          onClick={() => onPagina(Math.min(totalPaginas, pagina + 1))}
          disabled={pagina >= totalPaginas}
        >
          {t('reports.campaigns.nextPage')}
        </button>
      </div>
      )}

      <label className="rr-pagination-size">
        <span className="rr-sr-only">{t('reports.common.pageSize')}</span>
        <select
          className="rr-funnel-select"
          value={tamano}
          onChange={(e) => onTamano(Number(e.target.value))}
        >
          {TAMANOS_DE_PAGINA.map((n) => (
            <option key={n} value={n}>{t('reports.common.perPage', { n })}</option>
          ))}
        </select>
      </label>
    </div>
  );
}
